<template>
  <div class="filters">
    <v-input class="filter" v-model="filters.name" :placeholder="$t('NAME')" :debounce="500" clearable />

    <v-select class="filter" v-model="filters.genre_id" :placeholder="$t('CATEGORY')" :options="genresOptions"
      clearable />

    <v-input class="filter" v-model="filters.developer_name" :placeholder="$t('DEVELOPER')" :debounce="500" clearable />

    <template v-if="BRAND_CONFIG.domain !== 'dbservices.to'">
      <v-select class="filter" v-model="filters.type" :placeholder="$t('TYPE')" :options="typesOptions" clearable />
    </template>


    <v-select class="filter" v-model="filters.compatibility" :placeholder="$t('DEVICE')" :options="devicesOptions"
      clearable />

    <template v-if="BRAND_CONFIG.domain !== 'dbservices.to'">
      <v-input class="filter" v-model="filters.source_name" :placeholder="$t('SOURCE')" :debounce="500" clearable />
    </template>
  </div>
</template>

<script>
import VInput from '@/components/VInput.vue';
import VSelect from '@/components/VSelect.vue';
import { mapState } from 'vuex';
import { clone, isEqual } from 'lodash';
import { operatingSystems } from '@/data';

export default {
  components: {
    VInput,
    VSelect,
  },
  props: {
    value: Object,
    saveToUrl: Boolean,
    initialFilters: Object,
  },
  data() {
    return {
      isActive: true,
      filters: {
        name: '',
        genre_id: null,
        developer_name: '',
        type: '',
        compatibility: '',
        source_name: '',
      },
      devicesOptions: operatingSystems.map(({
        name,
        device,
      }) => ({
        label: device,
        value: name,
      })),
    };
  },
  computed: {
    ...mapState(['genres']),
    genresOptions() {
      return Object.values(this.genres.official)
        .map(genre => ({
          label: genre.name,
          value: genre.id,
        }));
    },
    typesOptions() {
      return ['official_app', 'user_app', 'repo_app'/* , 'enhancement' */].map(type => ({
        label: this.$t(type.toUpperCase()),
        value: type,
      }));
    },
  },
  methods: {
    trySetUrl() {
      if (!this.saveToUrl) {
        return;
      }

      const filtersQuery = Object.fromEntries(Object.entries(this.filters)
        .filter(([, value]) => value)
        .map(([key, value]) => [key, value.toString()]));
      const notFiltersQuery = Object.fromEntries(Object.entries(this.$route.query)
        .filter(([param]) => !(param in this.filters)));
      const newQuery = { ...filtersQuery, ...notFiltersQuery };
      if (isEqual(this.$route.query, newQuery)) {
        return;
      }

      this.$router.push({
        path: this.$route.path,
        query: newQuery,
      });
    },
    tryParseUrl() {
      if (!this.saveToUrl) {
        return;
      }

      const currentFilters = this.filters;
      const newFilters = clone(currentFilters);

      for (const filter in newFilters) {
        newFilters[filter] = this.parseQueryParam(this.$route.query, filter);
      }

      if (isEqual(currentFilters, newFilters)) {
        return;
      }

      this.filters = newFilters;
    },
    parseQueryParam(query, param) {
      switch (param) {
        case 'genre_id':
          return +query[param] || null;
        default:
          return query[param] || '';
      }
    },
    trySetInitialFilters() {
      if (!this.initialFilters) {
        return;
      }

      if (Object.values(this.filters)
        .some(Boolean)) {
        return;
      }

      for (const filter in this.initialFilters) {
        if (filter in this.filters) {
          this.filters[filter] = this.initialFilters[filter];
        }
      }
    },
  },
  watch: {
    isActive() {
      if (this.isActive) {
        this.tryParseUrl();
      }
    },
    '$route.query': {
      handler() {
        if (this.isActive) {
          this.tryParseUrl();
        }
      },
      deep: true,
    },
    value: {
      handler() {
        if (isEqual(this.filters, this.value)) {
          return;
        }

        for (const filter in this.value) {
          this.filters[filter] = this.value[filter];
        }
      },
      deep: true,
      immediate: true,
    },
    filters: {
      handler() {
        this.$emit('input', clone(this.filters));
        this.trySetUrl();
      },
      deep: true,
    },
  },
  created() {
    this.tryParseUrl();
    this.trySetInitialFilters();
  },
  activated() {
    this.isActive = true;
  },
  deactivated() {
    this.isActive = false;
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.filters {
  display: flex;
  gap: 10px;

  @include container(search, 1300px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 1fr;
  }
}

.filter {
  flex: 1 0 0;
  min-width: 170px;

  @include container(search, 1300px) {
    min-width: unset;
  }
}
</style>
